import {isEmpty} from 'lodash-es';
import type {ReactElement} from 'react';
import {useMemo, useState} from 'react';

import {Button} from '@/components';
import type {BusinessLimit} from '@/types';
import {BusinessFeeComparator, BusinessLimitField} from '@/types';

import LimitFormModal from './LimitFormModal';

type LimitUpdateButtonProps = {
  initialData?: BusinessLimit;
  businessId?: string;
  dataCy?: string;
};

const LimitUpdateButton = ({
  initialData,
  businessId,
  dataCy,
}: LimitUpdateButtonProps): ReactElement => {
  const [showFullScreen, setShowFullScreen] = useState(false);
  const updateLabel = isEmpty(initialData) ? 'Create Limit +' : 'Update Limit';
  const toggleFullScreen = () => {
    setShowFullScreen(!showFullScreen);
  };

  const data = useMemo<BusinessLimit | undefined>(() => {
    if (!businessId) {
      return initialData;
    }
    const base = initialData ?? {
      name: '',
      limit: {},
      meta: {flags: {isDeactivated: false}, tags: {}},
    };
    return {
      ...base,
      meta: {
        ...base.meta,
        tags: {
          ...base.meta?.tags,
          // Business tags use the shared `COMPARATOR::value` shape the server
          // validator enforces and the tab filter parses.
          [BusinessLimitField.Business]: `${BusinessFeeComparator.Equals}::${businessId}`,
        },
      },
    };
  }, [initialData, businessId]);

  return showFullScreen ? (
    <LimitFormModal
      isOpen={showFullScreen}
      dataCy={dataCy ? `${dataCy}-form` : undefined}
      onClose={toggleFullScreen}
      initialData={data}
      enforceBusinessTag={businessId}
    />
  ) : (
    <Button
      dataCy={dataCy ? `${dataCy}-open-form` : undefined}
      variant="solid"
      colorScheme="cyan"
      onClick={toggleFullScreen}
    >
      {updateLabel}
    </Button>
  );
};

export default LimitUpdateButton;
